import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { apiRequest } from '@/lib/queryClient';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  category: string;
  author: string;
  authorAvatar: string;
  imageUrl: string;
  publishedAt: string;
  readTime: number;
}

interface BlogFormData {
  title: string;
  content: string;
  excerpt: string;
  category: string;
  author: string;
  authorAvatar: string;
  imageUrl: string;
  readTime: number;
}

export default function BlogManager() {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [slugInput, setSlugInput] = useState("");
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [formData, setFormData] = useState<BlogFormData>({
    title: '',
    content: '',
    excerpt: '',
    category: 'Technology',
    author: 'VariableSoft Team',
    authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
    imageUrl: '',
    readTime: 5
  });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!coverImage) {
      setCoverPreview('');
      return;
    }
    const url = URL.createObjectURL(coverImage);
    setCoverPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [coverImage]);

  const { data: blogPosts = [], isLoading } = useQuery({
    queryKey: ['/api/blog-posts'],
    retry: false,
  });

  const resetForm = () => {
    setCoverImage(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      category: 'Technology',
      author: 'VariableSoft Team',
      authorAvatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
      imageUrl: '',
      readTime: 5
    });
  };

  const categories = ['Technology', 'Development', 'Design', 'Business', 'Innovation'];

  // Simple slugify helper
  const slugify = (v: string) => v
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

  const uploadCoverImage = async () => {
    if (!coverImage) return formData.imageUrl;
    const body = new FormData();
    body.append('image', coverImage);
    const response = await apiRequest('POST', '/api/admin/blog-posts/upload-image', body);
    const result = await response.json();
    if (typeof result.url !== 'string' || !result.url.startsWith('/uploads/blog/')) {
      throw new Error('Invalid image upload response. Please try again.');
    }
    // Retain the uploaded URL for retries if saving the post subsequently fails.
    setFormData(p => ({ ...p, imageUrl: result.url }));
    setCoverImage(null);
    return result.url as string;
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        title: formData.title.trim(),
        slug: slugInput || slugify(formData.title),
        content: formData.content.trim(),
        excerpt: formData.excerpt.trim(),
        category: formData.category,
        author: formData.author.trim(),
        authorAvatar: formData.authorAvatar.trim(),
        imageUrl: formData.imageUrl.trim(),
        readTime: Number(formData.readTime) || 1,
        isPublished: true,
      };

      // Basic client-side validation
      for (const [k, v] of Object.entries(payload)) {
        if (['metaTitle','metaDescription','metaKeywords','ogImage','isPublished','readTime','slug'].includes(k)) continue;
        if (k === 'imageUrl' && coverImage) continue;
        if (typeof v === 'string' && !v) {
          throw new Error(`Field "${k}" is required`);
        }
      }

      payload.imageUrl = await uploadCoverImage();
      const res = await apiRequest('POST', '/api/admin/blog-posts', payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Post created', description: 'Your blog post has been published.' });
      setIsCreateDialogOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['/api/blog-posts'] });
    },
    onError: (err: any) => {
      const message = err?.message || 'Failed to create post';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editingPost) throw new Error('No post selected');
      const payload = {
        title: formData.title.trim(),
        slug: slugInput || editingPost.slug,
        content: formData.content.trim(),
        excerpt: formData.excerpt.trim(),
        category: formData.category,
        author: formData.author.trim(),
        authorAvatar: formData.authorAvatar.trim(),
        imageUrl: formData.imageUrl.trim(),
        readTime: Number(formData.readTime) || 1,
        isPublished: true,
      };
      payload.imageUrl = await uploadCoverImage();
      const res = await apiRequest('PUT', `/api/admin/blog-posts/${editingPost.id}`, payload);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Post updated', description: 'Your blog post has been updated.' });
      setIsCreateDialogOpen(false);
      setEditingPost(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['/api/blog-posts'] });
    },
    onError: (err: any) => {
      const message = err?.message || 'Failed to update post';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (post: BlogPost) => {
      const res = await apiRequest('DELETE', `/api/admin/blog-posts/${post.id}`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Post deleted', description: 'The blog post has been removed.' });
      queryClient.invalidateQueries({ queryKey: ['/api/blog-posts'] });
    },
    onError: (err: any) => {
      const message = err?.message || 'Failed to delete post';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  const openCreate = () => {
    resetForm();
    setSlugInput("");
    setEditingPost(null);
    setIsCreateDialogOpen(true);
  };

  const openEdit = (post: BlogPost) => {
    setCoverImage(null);
    setEditingPost(post);
    setIsCreateDialogOpen(true);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      category: post.category,
      author: post.author,
      authorAvatar: post.authorAvatar,
      imageUrl: post.imageUrl,
      readTime: post.readTime,
    });
    setSlugInput(post.slug);
  };

  const onChangeTitle = (v: string) => {
    setFormData(p => ({ ...p, title: v }));
    if (!editingPost) setSlugInput(slugify(v));
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Blog Posts</h2>
          <p className="text-slate-600 dark:text-slate-300">Manage your blog content</p>
        </div>
        <Dialog open={isCreateDialogOpen || !!editingPost} onOpenChange={(open) => {
          if (isSaving) return;
          if (!open) {
            setIsCreateDialogOpen(false);
            setEditingPost(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button onClick={openCreate}>
              <i className="fas fa-plus mr-2"></i>
              Create Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPost ? 'Edit' : 'Create'} Blog Post</DialogTitle>
              <DialogDescription>{editingPost ? 'Update your article details' : 'Publish a new article to your blog'}</DialogDescription>
            </DialogHeader>

            <fieldset disabled={isSaving} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="title">Title</Label>
                <Input id="title" value={formData.title} onChange={(e) => onChangeTitle(e.target.value)} required />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="slug">Slug</Label>
                <Input id="slug" value={slugInput} onChange={(e) => setSlugInput(slugify(e.target.value))} readOnly={!editingPost} className={!editingPost ? "bg-slate-50 dark:bg-slate-800" : ""} />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData(p => ({...p, category: v}))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="readTime">Read Time (min)</Label>
                <Input id="readTime" type="number" min={1} value={formData.readTime} onChange={(e) => setFormData(p => ({...p, readTime: Number(e.target.value)}))} />
              </div>
              <div>
                <Label htmlFor="author">Author</Label>
                <Input id="author" value={formData.author} onChange={(e) => setFormData(p => ({...p, author: e.target.value}))} />
              </div>
              <div>
                <Label htmlFor="authorAvatar">Author Avatar URL</Label>
                <Input id="authorAvatar" value={formData.authorAvatar} onChange={(e) => setFormData(p => ({...p, authorAvatar: e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="coverImage">Cover Image</Label>
                <Input
                  id="coverImage"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  aria-describedby="coverImageHelp"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
                      e.target.value = '';
                      toast({ title: 'Invalid cover image', description: 'Choose a JPEG, PNG, GIF or WebP image up to 5 MB.', variant: 'destructive' });
                      return;
                    }
                    setCoverImage(file);
                  }}
                />
                <p id="coverImageHelp" className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  JPEG, PNG, GIF or WebP, up to 5 MB. {editingPost ? 'Choose a new image to replace the current cover, or keep the existing image.' : 'The image will upload when you publish.'}
                </p>
                {(coverPreview || formData.imageUrl) && (
                  <img src={coverPreview || formData.imageUrl} alt="Cover image preview" className="mt-3 max-h-48 rounded-md object-contain" />
                )}
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="excerpt">Excerpt</Label>
                <Textarea id="excerpt" rows={3} value={formData.excerpt} onChange={(e) => setFormData(p => ({...p, excerpt: e.target.value}))} />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="content">Content</Label>
                <Textarea id="content" rows={8} value={formData.content} onChange={(e) => setFormData(p => ({...p, content: e.target.value}))} />
              </div>
            </fieldset>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" type="button" disabled={isSaving} onClick={() => { setIsCreateDialogOpen(false); setEditingPost(null); resetForm(); }}>Cancel</Button>
              {editingPost ? (
                <Button onClick={() => updateMutation.mutate()} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Updating…' : 'Update'}
                </Button>
              ) : (
                <Button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Publishing…' : 'Publish'}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid gap-4">
          {Array.isArray(blogPosts) && blogPosts.map((post: BlogPost) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                          {post.title}
                        </h3>
                        <Badge variant="secondary">{post.category}</Badge>
                      </div>
                      <p className="text-slate-600 dark:text-slate-300 mb-3 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center space-x-4 text-sm text-slate-500 dark:text-slate-400">
                        <span>By {post.author}</span>
                        <span>{post.readTime} min read</span>
                        <span>{format(new Date(post.publishedAt), 'MMM dd, yyyy')}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button size="sm" variant="outline" onClick={() => openEdit(post)}>
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (confirm(`Delete post "${post.title}"?`)) {
                            deleteMutation.mutate(post);
                          }
                        }}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
          
          {(!Array.isArray(blogPosts) || blogPosts.length === 0) && (
            <Card>
              <CardContent className="p-8 text-center">
                <i className="fas fa-blog text-4xl text-slate-300 dark:text-slate-600 mb-4"></i>
                <p className="text-slate-600 dark:text-slate-300">No blog posts yet. Create your first post!</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
